import { finalizePendingSku } from '@/lib/skuPending';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Hash, Paperclip, FilePdf, Link as LinkIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import LookupCombobox from '@/components/ui/lookup-combobox';
import RemoteSelect from '@/components/ui/remote-select';
import { SkuWizard } from '@/components/SkuWizard';
import MasterItemPicker from '@/components/master/MasterItemPicker';
import { FormSection, FieldHint, RequiredHints, TechsReadinessPanel } from '@/components/registration';
import { MissingSkuGroupNote, NO_SKU_GROUP_MESSAGE } from '@/components/registration/MissingSkuGroupNote';
import { useAuth } from '@/auth/AuthContext';
import MaterialLines, { isResolved as isResolvedMaterial } from './development/MaterialLines';
import { groupFromSku } from '@/lib/skuGroup';
import { StorageThumb, StorageLink } from '@/components/StorageMedia';
import { engApi } from './engApi';
import { SimilarAssetHint } from './development/SimilarAssetHint';

// マスタ取得ラッパー（RemoteSelect 用）
const fetchOrderCategories = () => engApi.orderCategoriesList();
const fetchLocations = () => engApi.locations();
const fetchSuppliers = () => engApi.suppliersList();

// 部品受注入力と同じ入力ルール:
// マスター検索 → 自動入力 / 品番はテンプレート発番（手入力不可）/ 図番・メーカー品番・材質 / 添付ファイル
const empty = () => ({
  item_id: '', name: '', sku: '', sku_template_code: '', drawing_no: '', maker: '', maker_pn: '', model_no: '', material: '',
  qty_total: 0, uom: '個', unit_price: '',
  location: '', shelf_id: '',
  acquisition_type: 'existing', sku_group_id: '', asset_category_id: '',
  supplier_id: '', supplier_name: '',
  category_id: '', category_name: '',
  reference_url: '',
  photo_url: '', photo_filename: '', photo_is_pdf: false, notes: '',
});

// 必須項目 (品名/数量/単位/品番/型式/品番グループ/現在地/受注分類)
// 資産カテゴリは品番グループから自動決定されるため、ユーザーには尋ねない。
const REQ_LABEL = {
  name: '品名', qty_total: '数量', uom: '単位',
  sku: '品番 / 資産番号', model_no: '型式・仕様',
  sku_group_id: '品番グループ',
  category_id: '受注分類', shelf_id: '設置場所',
};

export const EngAssetFormDialog = ({ open, onClose, editing, onSaved, prefill, sourceRef }) => {
  const [form, setForm] = useState(empty());
  const [showOrderManual, setShowOrderManual] = useState(false);
  const [saving, setSaving] = useState(false);
  const [skuWizardOpen, setSkuWizardOpen] = useState(false);
  const skuPendingRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [groups, setGroups] = useState([]);
  const [manualGroup, setManualGroup] = useState(false);
  // 2026-10-28 · 決めるのは「品番ルール」ひとつ。グループとカテゴリはそこから決まる。
  const [ruleLabel, setRuleLabel] = useState('');
  // 2026-11-03 · 選んだ品番ルールに品番グループが無い（＝どの台帳でも登録できない）。
  const [ruleGroupMissing, setRuleGroupMissing] = useState(false);
  const [materials, setMaterials] = useState([]);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // §8.10 共通採番（ENG/CSM・ENG/EQP）から採るときの選択。番号は保存時にサーバが刻む。
  const [policyRow, setPolicyRow] = useState(null);
  const [sharedPick, setSharedPick] = useState(null);
  // master-search で既存の技術資産が当たったときの警告（自動選択・自動統合はしない）
  const [existingAsset, setExistingAsset] = useState(null);
  // ＊ は新規登録のときだけ出す。編集では save() の `if (!editing)` と同じ扱いで強制せず、
  // バックエンドの update_asset もこれらを要求しないため、必須だと言ってはいけない。
  const createMark = editing ? null : <span className="text-rose-500">＊</span>;

  useEffect(() => {
    if (!open) return;
    Promise.all([engApi.assetCategories(), engApi.skuGroups()])
      .then(([c, g]) => {
        setCats(c || []);
        // 設備・工具は 設備台帳（eng_equipment）の持ち物。その品番グループは
        // 選ばせない（送信後に弾くのではなく、選択肢に出さない）。
        // どのカテゴリが他台帳のものかはサーバが owner_ledger で教えてくれる
        const foreign = new Set((c || []).filter((x) => x.foreign_ledger).map((x) => x.id));
        setGroups((g || []).filter((x) => !foreign.has(x.default_category_id)));
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSharedPick(null);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/sku/number-policy`)
      .then((r) => setPolicyRow((r.data?.registers || [])
        .find((x) => x.register === 'eng_asset') || null))
      .catch(() => setPolicyRow(null));
  }, [open]);

  const sharedSeries = useMemo(() => (policyRow?.shared_prefixes || []).map((p) => ({
    prefix: p,
    label: policyRow?.series_labels?.[p] || p,
    next: policyRow?.shared_next?.[p] || '',
  })), [policyRow]);

  useEffect(() => {
    if (!open) return;
    setExistingAsset(null);
    // 必要部品から引き継いだ品番グループは「提案」なので、自動確定の表示ではなく
    // 編集できるドロップダウンで出す（昇格する人が変えられる）。
    setManualGroup(!editing && !!prefill?.sku_group_id);
    setForm(editing ? {
      acquisition_type: editing.acquisition_type || 'existing',
      item_id: editing.item_id || '', name: editing.name || '', sku: editing.sku || '',
      drawing_no: editing.drawing_no || '', maker: editing.maker || '', maker_pn: editing.maker_pn || '',
      model_no: editing.model_no || '', material: editing.material || '',
      qty_total: editing.qty_total ?? 0, uom: editing.uom || '個',
      unit_price: editing.unit_price ?? '',
      location: editing.location || '', shelf_id: editing.shelf_id || '',
      sku_group_id: editing.sku_group_id || '', asset_category_id: editing.asset_category_id || '',
      supplier_id: editing.supplier_id || '', supplier_name: editing.supplier_name || '',
      category_id: editing.category_id || '', category_name: editing.category_name || '',
      reference_url: editing.reference_url || '',
      photo_url: editing.photo_url || '', photo_filename: editing.photo_filename || '',
      photo_is_pdf: !!editing.photo_is_pdf, notes: editing.notes || '',
    } : { ...empty(), ...(prefill || {}) });
    setMaterials(((editing?.material_lines) || []).map((m) => ({ ...m, qty: String(m.qty) })));
  }, [open, editing, prefill]);

  // 品番が決まれば品番グループ・資産カテゴリも決まる（手動修正中は上書きしない）
  useEffect(() => {
    if (manualGroup || !groups.length) return;
    const code = groupFromSku(form.sku, groups);
    if (code && code !== form.sku_group_id) {
      const g = groups.find((x) => x.code === code);
      setForm((f) => ({
        ...f, sku_group_id: code,
        asset_category_id: g?.default_category_id || f.asset_category_id || '',
      }));
    }
  }, [form.sku, form.sku_group_id, groups, manualGroup]);

  // 必要部品から引き継いだ品番グループには資産カテゴリが付いていないので、
  // グループ一覧が来た時点で既定カテゴリを補う（値は変えない、空欄を埋めるだけ）。
  useEffect(() => {
    if (!groups.length || !form.sku_group_id || form.asset_category_id) return;
    const g = groups.find((x) => x.code === form.sku_group_id);
    if (g?.default_category_id) {
      setForm((f) => ({ ...f, asset_category_id: g.default_category_id }));
    }
  }, [groups, form.sku_group_id, form.asset_category_id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const patch = (p) => setForm((f) => ({ ...f, ...p }));
  // 2026-10-21 · 既存の物には TECHS発注の項目を出さない（編集は発注情報があるときだけ）
  const hasTechsData = !!(form.category_id || form.supplier_id || form.unit_price
    || form.reference_url);
  const showOrderFields = editing
    ? (form.acquisition_type === 'purchase' || hasTechsData || showOrderManual)
    : form.acquisition_type === 'purchase';
  // 2026-10-28 · 「自社製作」で入れたメーカーは、種類を変えたら外す。
  // 手で書いた値は残す（設備を登録 と同じ扱い）。
  const IN_HOUSE_MAKER = '自社製作（技術部）';
  const pickAcquisition = (v) => patch({
    acquisition_type: v,
    ...(v === 'in_house'
      ? (String(form.maker || '').trim() ? {} : { maker: IN_HOUSE_MAKER })
      : (form.maker === IN_HOUSE_MAKER ? { maker: '' } : {})),
  });
  // 編集するとマスターリンクを解除（部品受注入力と同じ挙動）
  const editField = (p) => setForm((f) => (f.item_id ? { ...f, ...p, item_id: '' } : { ...f, ...p }));

  const pickItem = (it) => {
    // 既存の技術資産がヒットした場合は「同じものがもう登録されている」警告に使う。
    // マスター品目リンク（item_id）は mfg_items 専用なので張らない。
    if (it.source === 'eng') {
      setExistingAsset(it);
      return;
    }
    setForm((f) => {
      const sku = f.sku || it.sku || it.model_no || '';
      const code = it.sku_group_id || groupFromSku(sku, groups) || f.sku_group_id || '';
      const g = groups.find((x) => x.code === code);
      return {
      ...f,
      item_id: it.id,
      sku_group_id: code,
      asset_category_id: g?.default_category_id || f.asset_category_id || '',
      name: f.name || it.name || '',
      sku,
      drawing_no: f.drawing_no || it.drawing_no || '',
      maker: f.maker || it.maker || '',
      maker_pn: f.maker_pn || it.maker_pn || '',
      model_no: f.model_no || it.model_no || '',
      uom: f.uom || it.uom || '個',
      reference_url: f.reference_url || it.reference_url || '',
      };
    });
    setManualGroup(false);
  };

  const clearItemLink = () => patch({ item_id: '' });

  // 品番グループが資産カテゴリの唯一の決定元。ユーザーは品番グループだけを選ぶ。
  const applyGroup = (code) => {
    setManualGroup(false);
    const g = groups.find((x) => x.code === code);
    setForm((f) => ({
      ...f,
      sku_group_id: code || '',
      asset_category_id: g?.default_category_id || '',
    }));
  };

  const handleSkuIssued = (sku, meta) => {
    setRuleLabel(meta?.template_name || meta?.template_code || '');
    // 品番グループはサーバの解決結果（services/sku_group_link）をそのまま使う。
    const ruleGroup = meta?.sku_group_code || '';
    setRuleGroupMissing(!ruleGroup);
    if (meta?.from_shared) {
      // 共通採番から出す（保存時にサーバが確定・画面では次の番号を見せるだけ）。
      skuPendingRef.current = null;
      setSharedPick({ prefix: meta.shared_prefix, next: sku || '',
        rule: meta.template_name || '' });
      patch({ sku: '', sku_template_code: meta.template_code || '' });
      const sharedGroup = ruleGroup || meta.shared_prefix;
      if (groups.some((g) => g.code === sharedGroup)) applyGroup(sharedGroup);
      toast.success(`共通採番から発番します: ${sku || meta.shared_prefix}（保存時に確定）`);
      return;
    }
    if (!sku) return;
    setSharedPick(null);
    // 品番テンプレートを form に保持することで、SkuWizard 再オープン時に同一テンプレートは
    // 再発番せず既存 SKU を再利用する (deselect → reselect で番号が跳ねる問題を防止)
    editField({ sku, sku_template_code: meta?.template_code || '' });
    if (ruleGroup && groups.some((g) => g.code === ruleGroup)) {
      applyGroup(ruleGroup, { forceCategory: false });
    }
    if (meta?.pending) {
      skuPendingRef.current = meta.pending;
      toast.success(`発番予定: ${sku} — 保存時に正式発番されます`);
    } else {
      skuPendingRef.current = null;
      toast.success(`品番を発番しました: ${sku}`);
    }
  };
  const resetSku = () => {
    skuPendingRef.current = null;
    setRuleGroupMissing(false);
    setSharedPick(null);
    setRuleLabel('');
    patch({ sku: '', sku_template_code: '' });
    toast.message('品番をクリアしました');
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const res = await engApi.uploadAssetFile(f);
      patch({ photo_url: res.photo_url, photo_filename: res.filename, photo_is_pdf: !!res.is_pdf });
      toast.success(res.is_pdf ? 'PDFを添付しました' : '画像を添付しました');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'ファイルのアップロードに失敗しました');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const clearPhoto = () => patch({ photo_url: '', photo_filename: '', photo_is_pdf: false });

  const save = async () => {
    // 必須項目は**新規登録のときだけ**強制する。既存の不完全な資産（現在 7件中6件）を
    // 編集するたびに全項目の穴埋めを強要すると、直したい1項目すら直せなくなる。
    const missing = [];
    if (!form.name.trim()) missing.push(REQ_LABEL.name);
    // 数量: 0 在庫（未入荷など）は許可し、負数のみ拒否
    if (form.qty_total === '' || form.qty_total === null || form.qty_total === undefined || Number.isNaN(Number(form.qty_total)) || Number(form.qty_total) < 0) missing.push(REQ_LABEL.qty_total);
    if (!(form.uom || '').trim()) missing.push(REQ_LABEL.uom);
    if (!editing) {
      if (!(form.sku || '').trim() && !sharedPick) missing.push(REQ_LABEL.sku);
      if (!(form.model_no || '').trim()) missing.push(REQ_LABEL.model_no);
      if (!form.sku_group_id) {
        // ルールにグループが無いときは「必須項目」ではなく、何をすればよいかを出す。
        if (ruleGroupMissing) {
          toast.error(NO_SKU_GROUP_MESSAGE);
          return;
        }
        missing.push(REQ_LABEL.sku_group_id);
      }
      // 受注分類は「これから購入する」物だけ（画面に出ていない項目で止めない）
      if (showOrderFields && !form.category_id) missing.push(REQ_LABEL.category_id);
      if (!form.shelf_id) missing.push(REQ_LABEL.shelf_id);
    }
    if (missing.length) {
      toast.error(`必須項目を入力してください: ${missing.map((m) => `「${m}」`).join('、')}`);
      return;
    }
    setSaving(true);
    let finalSku = form.sku.trim();
    if (skuPendingRef.current) {
      try {
        finalSku = await finalizePendingSku(skuPendingRef.current);
        skuPendingRef.current = null;
        patch({ sku: finalSku });
      } catch (e) {
        toast.error(e?.response?.data?.detail || '品番の正式発番に失敗しました');
        setSaving(false);
        return;
      }
    }
    try {
      const body = {
        name: form.name.trim(), sku: finalSku || null,
        ...(!finalSku && sharedPick
          ? { sku_mode: 'shared', shared_prefix: sharedPick.prefix }
          : {}),
        item_id: form.item_id || null,
        drawing_no: form.drawing_no.trim() || null,
        maker: form.maker.trim() || null, maker_pn: form.maker_pn.trim() || null,
        model_no: form.model_no.trim() || null, material: form.material.trim() || null,
        qty_total: Number(form.qty_total),
        uom: form.uom.trim() || '個',
        unit_price: form.unit_price === '' ? null : Number(form.unit_price),
        location: (form.location || '').trim() || null,
        shelf_id: form.shelf_id || null,
        sku_group_id: form.sku_group_id || null,
        asset_category_id: form.asset_category_id || null,
        supplier_id: form.supplier_id || null,
        supplier_name: (form.supplier_name || '').trim() || null,
        reference_url: form.reference_url.trim() || null,
        acquisition_type: form.acquisition_type || 'existing',
        category_id: form.category_id || null,
        category_name: (form.category_name || '').trim() || null,
        photo_url: form.photo_url || null, photo_filename: form.photo_filename || null,
        photo_is_pdf: !!form.photo_is_pdf,
        notes: form.notes.trim() || null,
        // 自社製作の物が使う材料（1個あたり）。購入品では送らない。
        material_lines: form.acquisition_type === 'in_house'
          ? materials.filter(isResolvedMaterial).map((l) => ({
            component_item_id: l.component_item_id,
            component_source: l.component_source,
            qty: Number(l.qty),
            qty_basis: l.qty_basis || 'per_unit',
            uom: l.uom,
            notes: (l.notes || '').trim() || null,
          }))
          : [],
        // 由来（新規登録のときだけ）: どの開発案件のどの必要部品から昇格したか
        ...(editing ? {} : {
          source_development_id: sourceRef?.development_id || null,
          source_part_id: sourceRef?.part_id || null,
        }),
      };
      const res = editing ? await engApi.updateAsset(editing.id, body) : await engApi.createAsset(body);
      toast.success(editing ? '更新しました' : '登録しました');
      onSaved?.(res);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  // 要入力の案内（新規のときだけ・警告のみ）。保存を止める条件は save() が持つ。
  const missing = editing ? [] : [
    [!form.name.trim(), REQ_LABEL.name],
    // 品番・品番グループは「品番ルール」ひとつの結果なので、案内もひとつにする
    [!(form.sku || '').trim() && !sharedPick, '品番ルール（品番を発番します）'],
    [!(form.model_no || '').trim(), REQ_LABEL.model_no],
    [!!(form.sku || sharedPick) && !form.sku_group_id, REQ_LABEL.sku_group_id],
    [showOrderFields && !form.category_id, REQ_LABEL.category_id],
    [!(form.uom || '').trim(), REQ_LABEL.uom],
    [!form.shelf_id, '設置場所'],
  ].filter(([cond]) => cond).map(([, label]) => label);


  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="eng-asset-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? '資産を編集' : '資産を登録'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <RequiredHints missing={missing} title="要入力（登録前に入れてください）" testId="eng-form-required-hints" />

          {/* 1 ── 基本情報 ─────────────────────────────────────────────── */}
          {!editing && (
            <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3" data-testid="eng-form-acq">
              <p className="text-xs font-semibold text-zinc-700">登録の種類</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {[['existing', '既存（すでに社内にある）'], ['purchase', 'これから購入する'],
                  ['in_house', '自社製作（開発案件から）']].map(([v, label]) => (
                    <button key={v} type="button" aria-pressed={form.acquisition_type === v}
                      onClick={() => pickAcquisition(v)}
                      data-testid={`eng-form-acq-${v}`}
                      className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
                        form.acquisition_type === v
                          ? 'border-teal-600 bg-teal-600 text-white'
                          : 'border-zinc-300 bg-white text-zinc-700 hover:border-teal-400'}`}>
                      {label}
                    </button>
                  ))}
              </div>
              <p className="mt-1.5 text-[11px] text-zinc-500" data-testid="eng-form-acq-note">
                {form.acquisition_type === 'purchase'
                  ? '発注に必要な項目（受注分類・仕入先・注文URL・メーカー品番）も聞きます。'
                  : form.acquisition_type === 'in_house'
                    ? 'メーカーは「自社製作（技術部）」になります。'
                    : 'すでに社内にある物です。発注の項目は聞きません。'}
              </p>
            </div>
          )}

          {editing && !showOrderManual && !hasTechsData && (
            <button type="button" onClick={() => setShowOrderManual(true)}
              className="self-start text-[11px] font-semibold text-teal-700 hover:text-teal-900"
              data-testid="eng-form-add-order-info">
              購入情報を追加（TECHS発注の項目を表示）
            </button>
          )}

          <FormSection title="基本情報" testId="eng-form-section-basic">
          {/* 分類 — 決めるのは「品番ルール」ひとつ。グループとカテゴリはそこから決まる */}
          {(() => {
            const autoCode = form.sku_group_id;
            const grp = groups.find((g) => g.code === autoCode);
            const catName = (cats.find((c) => c.id === form.asset_category_id) || {}).name_ja;
            const fromMaster = !!form.item_id;
            return (
              <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-3 space-y-2"
                data-testid="eng-form-classify">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-[10px] font-semibold text-zinc-500">
                      品番ルール {fromMaster ? '' : createMark}
                    </p>
                    <p className="text-sm font-medium text-zinc-900 mt-0.5"
                      data-testid="eng-form-rule-label">
                      {fromMaster
                        ? `マスターの品番をそのまま使います（${form.sku || '—'}）`
                        : (ruleLabel || sharedPick?.rule
                          || (form.sku ? `発番済み: ${form.sku}` : '未選択'))}
                    </p>
                  </div>
                  {!fromMaster && (
                    <Button type="button" variant="outline" className="h-9 px-3 shrink-0"
                      onClick={() => setSkuWizardOpen(true)}
                      data-testid="eng-form-rule-pick">
                      <Hash size={14} className="mr-1.5" />
                      {ruleLabel || form.sku || sharedPick ? '品番ルールを変更' : '品番ルールから選ぶ'}
                    </Button>
                  )}
                </div>
                <div className="flex items-start gap-4 flex-wrap">
                  <div>
                    <p className="text-[10px] font-semibold text-zinc-500">品番グループ（自動）</p>
                    <p className="text-sm font-medium text-zinc-900 mt-0.5" data-testid="eng-form-auto-group">
                      {autoCode
                        ? `${autoCode}${grp?.name_ja ? ` — ${grp.name_ja}` : ''}`
                        : (ruleGroupMissing ? '未登録' : '品番ルールを選ぶと決まります')}
                    </p>
                  </div>
                  <div className="border-l border-teal-200 pl-4">
                    <p className="text-[10px] font-semibold text-zinc-500">資産カテゴリ（自動）</p>
                    <p className="text-sm font-medium text-zinc-900 mt-0.5" data-testid="eng-form-derived-category">
                      {autoCode
                        ? (catName || '未設定 — 品番グループに既定カテゴリを設定してください')
                        : (ruleGroupMissing ? '未登録' : '品番ルールを選ぶと決まります')}
                    </p>
                  </div>
                  {isAdmin && !manualGroup && (
                    <button type="button" onClick={() => setManualGroup(true)}
                      data-testid="eng-form-group-override"
                      className="ml-auto self-center text-[11px] font-medium text-zinc-500 underline hover:text-teal-800">
                      品番グループを修正
                    </button>
                  )}
                </div>
                {isAdmin && manualGroup && (
                  <div data-testid="eng-form-classify-manual">
                    <Label className="text-xs font-semibold text-zinc-600">品番グループ（管理者のみ）</Label>
                    <select value={form.sku_group_id || ''} onChange={(e) => applyGroup(e.target.value)}
                      className="mt-1 h-10 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm"
                      data-testid="eng-form-sku-group">
                      <option value="">選択してください</option>
                      {groups.map((g) => (
                        <option key={g.code} value={g.code}>{g.code} — {g.name_ja}</option>
                      ))}
                    </select>
                    <FieldHint>ふつうは品番ルールから決まります。合わないときだけ直してください。</FieldHint>
                  </div>
                )}
                {!autoCode && ruleGroupMissing && (
                  <MissingSkuGroupNote testId="eng-form-no-sku-group" />
                )}
                {form.sku && form.sku_group_id
                  && !String(form.sku).toUpperCase()
                    .replace(/^[A-Z0-9]{1,8}\s*\/\s*/, '').startsWith(form.sku_group_id) && (
                  <p className="rounded bg-amber-50 border border-amber-200 px-1.5 py-1 text-[10px] font-medium text-amber-700"
                    data-testid="eng-form-group-mismatch">
                    品番「{form.sku}」は {form.sku_group_id} で始まっていません。品番ルールと品番グループが一致しているか確認してください。
                  </p>
                )}
              </div>
            );
          })()}

          {/* マスター検索 — 必要部品を追加 と同一の共有コンポーネント */}
          <MasterItemPicker
            testIdPrefix="eng-form-item"
            label="品目（マスターから検索 または 新規入力）"
            placeholder="品名・品番で既存マスターから検索..."
            search={engApi.masterSearch}
            onPick={pickItem}
            linked={form.item_id ? { label: form.name, sku: form.sku, sourceLabel: 'マスターから' } : null}
            onUnlink={clearItemLink}
            unlinkLabel="解除"
            statusSlot={!form.item_id && (form.name || '').trim() ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                data-testid="eng-form-item-status">新規（未登録）</span>
            ) : null}
            renderResultExtra={(it) => (
              <span className={it.source === 'eng' ? 'font-semibold text-amber-700' : 'text-zinc-400'}
                data-testid={`eng-form-item-source-${it.id}`}>
                {it.source === 'eng' ? '既に技術資産にあります' : it.source_label}
              </span>
            )}
            helper="マスターから選ぶと品名・仕様が自動入力されます。編集すると新規扱いになります。"
          />
          {existingAsset && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2"
              data-testid="eng-form-existing-asset-warning">
              <p className="text-xs font-semibold text-amber-900">
                同じものがすでに技術資産に登録されています
              </p>
              <p className="text-xs text-amber-900">
                {existingAsset.name}
                {existingAsset.sku ? `（${existingAsset.sku}）` : ''}
                {existingAsset.on_hand != null ? ` · 在庫 ${existingAsset.on_hand}` : ''}
              </p>
              <button type="button" onClick={() => setExistingAsset(null)}
                className="mt-1 text-xs font-semibold text-amber-800 underline"
                data-testid="eng-form-existing-asset-dismiss">
                それでも新しく登録する
              </button>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold text-zinc-600">品名 <span className="text-rose-500">＊</span></Label>
            <Input value={form.name} onChange={(e) => editField({ name: e.target.value })} placeholder="例: M6 ステンレスボルト" className="h-10 mt-1" data-testid="eng-form-name" />
            {!editing && <SimilarAssetHint name={form.name} testid="eng-form-similar" />}
          </div>


          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">メーカー</Label>
              <LookupCombobox kind="makers" value={form.maker || ''} onChange={(v) => editField({ maker: v })}
                testId="eng-form-maker" inputClassName="mt-1 h-10" placeholder="例: KYOCERA" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">材質</Label>
              <LookupCombobox kind="materials" value={form.material || ''} onChange={(v) => editField({ material: v })}
                testId="eng-form-material" inputClassName="mt-1 h-10" placeholder="例: SUS304" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-zinc-600">図番</Label>
            <Input value={form.drawing_no} onChange={(e) => editField({ drawing_no: e.target.value })}
              placeholder="例: DWG-2026-042" className="h-10 mt-1 font-mono" data-testid="eng-form-drawingno" />
          </div>

          {/* 自社製作の物は「何から作るか」をここで持つ（型式に材料名を書かずに済む）。
              必要部品と手配に取り込まれ、案件ごとに打ち直さなくてよくなる。 */}
          {form.acquisition_type === 'in_house' && (
            <div data-testid="eng-form-materials">
              <Label className="text-xs font-semibold text-zinc-600">使用材料（1個あたり）</Label>
              <p className="text-[11px] text-zinc-500">
                この物を作るのに使う材料。開発案件の「必要部品と手配」に、必要数ぶんで取り込まれます。
              </p>
              <div className="mt-1">
                <MaterialLines lines={materials} onChange={setMaterials} />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-semibold text-zinc-600">備考</Label>
            <Textarea value={form.notes} onChange={set('notes')} rows={2} className="mt-1" data-testid="eng-form-notes" />
          </div>
          </FormSection>

          {/* 2 ── 品番・仕様（これから購入する物では TECHS発注の項目も聞く） ─── */}
          <FormSection tone="techs"
            title={showOrderFields ? 'TECHS発注に必要な情報' : '品番・仕様'}
            note={showOrderFields
              ? 'ここが埋まっていれば、部品受注入力からTECHSの部品受注入力・部品手配入力まで打ち直しなしで進みます。'
              : '発注の項目（受注分類・仕入先・注文URL・メーカー品番）は、これから購入する物のときだけ出ます。'}
            testId="eng-form-section-techs">
            <div>
              <Label className="text-xs font-semibold text-zinc-600">品番 / 資産番号 {createMark}</Label>
              <div className="mt-1 flex items-center gap-1">
                <Input
                  value={form.sku || (sharedPick ? `${sharedPick.next || sharedPick.prefix}（保存時に確定）` : '')}
                  readOnly
                  placeholder={form.item_id ? '' : 'テンプレートから発番してください'}
                  className="h-10 font-mono flex-1 bg-zinc-50 text-zinc-700"
                  data-testid="eng-form-sku"
                  title="品番はテンプレートから発番してください（手入力不可）"
                />
                {!form.item_id && (
                  <Button type="button" variant="outline" className="h-10 px-3 shrink-0"
                    onClick={() => setSkuWizardOpen(true)}
                    data-testid="eng-form-sku-issue" title="テンプレートから発番">
                    <Hash size={14} />
                  </Button>
                )}
                {!form.item_id && (form.sku || sharedPick) && (
                  <Button type="button" variant="ghost" className="h-10 px-2 shrink-0 text-zinc-400 hover:text-rose-600"
                    onClick={resetSku}
                    data-testid="eng-form-sku-reset" title="この品番を破棄して発番し直せるようにする">
                    ✕
                  </Button>
                )}
              </div>
              <FieldHint>
                {form.item_id
                  ? '既存マスターから引き当てた品番です。'
                  : sharedPick
                    ? `共通採番（${sharedPick.rule || sharedPick.prefix}）から保存時に発番します。番号は取り置きしないので飛び番になりません。`
                    : form.sku
                      ? '同じテンプレートを再選択しても同じ品番が使われます。別のテンプレートを使う場合は✕でクリアしてください。'
                      : '品番はテンプレートから発番してください（手入力不可）。TECHSの品番そのものになります。'}
              </FieldHint>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">型式・仕様 {createMark}</Label>
                <Input value={form.model_no} onChange={(e) => editField({ model_no: e.target.value })}
                  placeholder="例: SUS304 M6×20" className="h-10 mt-1 font-mono" data-testid="eng-form-model" />
                <FieldHint>TECHSの発注明細にそのまま載ります。仕入先が品物を特定する決め手です</FieldHint>
              </div>
              <div className={showOrderFields ? '' : 'hidden'}>
                <Label className="text-xs font-semibold text-zinc-600">メーカー品番</Label>
                <Input value={form.maker_pn} onChange={(e) => editField({ maker_pn: e.target.value })}
                  placeholder="例: KYO-A1-123" className="h-10 mt-1 font-mono" data-testid="eng-form-makerpn" />
                <FieldHint>分かる場合は入れてください。仕入先への型番照会が一度で済みます</FieldHint>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">単位 <span className="text-rose-500">＊</span></Label>
                <LookupCombobox kind="uoms" value={form.uom || ''} onChange={(v) => editField({ uom: v })}
                  testId="eng-form-uom" inputClassName="mt-1 h-10" />
                <FieldHint>TECHSの発注単位。個・本・mなど、注文する単位で入れます</FieldHint>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">数量 <span className="text-rose-500">＊</span></Label>
                <Input type="number" min="0" value={form.qty_total} onChange={set('qty_total')} className="h-10 mt-1 tabular-nums" data-testid="eng-form-qty" />
                <FieldHint>いま手元にある数。発注数はこの数を見て決めます</FieldHint>
                {Number(form.qty_total) === 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid="eng-form-qty-zero-badge">
                    在庫なし
                  </div>
                )}
                {/* 昇格（必要部品からの引き継ぎ）で0のときは理由を明示する。 */}
                {!editing && prefill && Number(form.qty_total) === 0 && (
                  <p className="mt-1 text-[11px] leading-snug text-amber-700" data-testid="eng-form-qty-promotion-hint">
                    計画に在庫の記録が無いため <strong>0</strong> です。必要数ではなく、
                    <strong>いま実際に手元にある数</strong>を入れてください。
                  </p>
                )}
              </div>
            </div>

            {showOrderFields && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">受注分類 {createMark}</Label>
                <div className="mt-1">
                  <RemoteSelect fetch={fetchOrderCategories} value={form.category_id || null}
                    onChange={(o) => patch({ category_id: o?.id || '', category_name: o?.name || '' })}
                    placeholder="受注分類を選択…" secondaryKey="code"
                    testId="eng-form-order-category"
                    emptyHint="受注分類マスタから追加してください" />
                </div>
                <FieldHint>TECHSの受注区分。発注の振り分けと集計に使われます</FieldHint>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">仕入先（注文先）</Label>
                <div className="mt-1">
                  <RemoteSelect fetch={fetchSuppliers} value={form.supplier_id || null}
                    onChange={(o) => patch({ supplier_id: o?.id || '', supplier_name: o?.name || '' })}
                    placeholder="仕入先を選択…" allowClear
                    testId="eng-form-supplier"
                    emptyHint="仕入先マスタから追加してください" />
                </div>
                <FieldHint>TECHSの発注先。分かる場合は入れておくと発注時に選び直す手間が減ります</FieldHint>
              </div>
            </div>
            )}

            {showOrderFields && (
              <div>
                <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1"><LinkIcon size={12} weight="bold" /> 注文URL</Label>
                <Input value={form.reference_url} onChange={set('reference_url')} placeholder="https://…" className="h-10 mt-1 font-mono text-xs" data-testid="eng-form-url" />
                <FieldHint>通販の商品ページ。次に買う人が同じ物を迷わず選べます</FieldHint>
              </div>
            )}

            {showOrderFields && (
              <TechsReadinessPanel value={{ ...form, order_category_id: form.category_id,
                order_category_name: form.category_name, qty: form.qty_total }}
                register="eng_consumable" deptCode="ENG" testId="eng-form-techs-check" />
            )}
          </FormSection>

          {/* 3 ── 保管・管理 ──────────────────────────────────────────── */}
          <FormSection title="保管・管理" testId="eng-form-section-keeping">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-zinc-600">設置場所 {createMark}</Label>
                <div className="mt-1">
                  <RemoteSelect fetch={fetchLocations} value={form.shelf_id || null}
                    secondaryKey="department_name"
                    onChange={(o) => patch({ shelf_id: o?.id || '', location: o?.name || '' })}
                    placeholder="建物 › 部屋 › 棚 を選択…"
                    testId="eng-form-location"
                    emptyHint="場所マスタから追加してください" />
                </div>
                <p className="mt-0.5 text-[10px] text-zinc-500">例: 本社 &gt; 技術室 &gt; 技術2</p>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">取得価格（円）</Label>
                <Input type="number" min="0" value={form.unit_price} onChange={set('unit_price')} className="h-10 mt-1 tabular-nums" data-testid="eng-form-price" />
              </div>
            </div>

            {/* 添付ファイル — parts request と同じ */}
            <div>
              <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1"><Paperclip size={12} weight="bold" /> 添付ファイル</Label>
              {form.photo_url ? (
                <div className="flex items-center gap-2 h-10 mt-1 px-3 rounded-md border border-zinc-200 bg-zinc-50" data-testid="eng-form-photo-preview">
                  {form.photo_is_pdf ? (
                    <StorageLink path={form.photo_url} target="_blank" rel="noreferrer"
                      className="w-7 h-7 rounded bg-rose-50 border border-rose-200 flex items-center justify-center hover:bg-rose-100"
                      title="PDFを開く" data-testid="eng-form-photo-open">
                      <FilePdf size={16} weight="duotone" className="text-rose-600" />
                    </StorageLink>
                  ) : (
                    <StorageLink path={form.photo_url} target="_blank" rel="noreferrer" title="画像を開く" data-testid="eng-form-photo-open">
                      <StorageThumb path={form.photo_url} alt="" className="w-7 h-7 rounded object-cover" />
                    </StorageLink>
                  )}
                  <span className="flex-1 text-xs text-zinc-600 truncate">{form.photo_filename || '添付済み'}</span>
                  <button onClick={clearPhoto} className="text-xs text-rose-600 hover:text-rose-800" data-testid="eng-form-photo-remove">削除</button>
                </div>
              ) : (
                <div className="mt-1">
                  <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.pdf" onChange={onUpload} className="hidden" data-testid="eng-form-photo-input" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                    className="h-10 w-full justify-start" data-testid="eng-form-photo-btn">
                    <Paperclip size={14} weight="bold" className="mr-1.5" />
                    {uploading ? 'アップロード中...' : '添付ファイル（写真・PDF）'}
                  </Button>
                </div>
              )}
            </div>
          </FormSection>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-700 hover:bg-teal-800" data-testid="eng-form-save">
            {saving ? '保存中…' : '保存'}
          </Button>
        </DialogFooter>
        {/* 20260610 · scoped to the 技術資産 app (sku_apps.code=engineering). */}
        <SkuWizard open={skuWizardOpen} onOpenChange={setSkuWizardOpen} onIssued={handleSkuIssued}
          deptLabel={policyRow?.dept_label || ''} sharedSeries={sharedSeries}
          defaultApp="engineering"
          preferPrefix={form.sku_group_id || ''}
          currentSku={form.sku || ''} currentTemplateCode={form.sku_template_code || ''} />
      </DialogContent>
    </Dialog>
  );
};
